<template>
  <!-- Filter Trigger Button -->
  <div class="filter-popup-wrapper">
    <!-- Using <button> for full keyboard & AT support (WCAG 4.1.2) -->
    <button
      id="filter-trigger-btn"
      class="filter-trigger"
      :class="{ active: isOpen, selected: hasActiveFilters }"
      type="button"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      aria-controls="filter-panel"
      @click="togglePopup"
    >
      <span class="trigger-content">
        <span
          v-if="activeFiltersCount > 0"
          class="filter-counter"
          aria-hidden="true"
        >
          {{ activeFiltersCount }}
        </span>
        <!-- Screen-reader-friendly label that includes the count (WCAG 1.3.1) -->
        <span class="sr-only" v-if="activeFiltersCount > 0">
          All Filters, {{ activeFiltersCount }} active
        </span>
        <span class="filter-text" aria-hidden="activeFiltersCount > 0"
          >All Filters</span
        >
      </span>
      <!-- Decorative arrow SVG hidden from AT (WCAG 1.1.1) -->
      <svg
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- Overlay: decorative backdrop, dismissed on click (WCAG 2.1.1) -->
    <div
      v-show="isOpen"
      class="overlay"
      role="presentation"
      @click="closePopup"
    ></div>

    <!-- Panel Filter -->
    <Transition name="filter-popup">
      <div
        v-show="isOpen"
        id="filter-panel"
        class="zz-filter-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="filter-panel-title"
      >
        <!-- Close button for mobile -->
        <div class="filter-panel-header">
          <h3 id="filter-panel-title" class="filter-panel-title">Filters</h3>
          <!-- Accessible close button (WCAG 4.1.2) -->
          <button
            class="close-button"
            type="button"
            aria-label="Close filters"
            @click="closePopup"
          >
            <!-- Decorative X icon -->
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <div style="margin-bottom: 20px" class="zz-filter-panel-content">
          <!-- Grades Section: fieldset+legend groups related checkboxes (WCAG 1.3.1) -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Grades</legend>
            <div class="underline" aria-hidden="true"></div>
            <div
              v-for="option in gradeOptions"
              :key="option.value"
              class="option-label"
              :class="{ 'disabled-item': option.disabled }"
            >
              <template v-if="option.value === 'all'">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    @change="handleAllSelection('grade', $event)"
                    :checked="allSelected.grade"
                  />
                  <span class="margin-label">All Grades</span>
                </label>
              </template>
              <template v-else>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    v-model="localGrades"
                    :value="option.value"
                    @change="emitFilters"
                    :disabled="option.disabled"
                    :aria-label="
                      option.disabled
                        ? option.title + ' (unavailable)'
                        : undefined
                    "
                  />
                  <span class="margin-label">{{ option.title }}</span>
                </label>
              </template>
            </div>
          </fieldset>

          <!-- Subjects Section -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Subjects</legend>
            <div class="underline" aria-hidden="true"></div>
            <div
              v-for="option in subjectOptions"
              :key="option.value"
              class="option-label"
              :class="{ 'disabled-item': option.disabled }"
            >
              <template v-if="option.value === 'all'">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    @change="handleAllSelection('subject', $event)"
                    :checked="allSelected.subject"
                  />
                  <span class="margin-label">All Subjects</span>
                </label>
              </template>
              <template v-else>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    v-model="localSubjects"
                    :value="option.value"
                    @change="emitFilters"
                    :disabled="option.disabled"
                    :aria-label="
                      option.disabled
                        ? option.title + ' (unavailable)'
                        : undefined
                    "
                  />
                  <span class="margin-label">{{ option.title }}</span>
                </label>
              </template>
            </div>
          </fieldset>

          <!-- Focus Section -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Focus</legend>
            <div class="underline" aria-hidden="true"></div>
            <div
              v-for="option in focusOptions"
              :key="option.value"
              class="option-label"
              :class="{ 'disabled-item': option.disabled }"
            >
              <template v-if="option.value === 'all'">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    @change="handleAllSelection('focus', $event)"
                    :checked="allSelected.focus"
                  />
                  <span class="margin-label">All Focus</span>
                </label>
              </template>
              <template v-else>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    v-model="localFocus"
                    :value="option.value"
                    @change="emitFilters"
                    :disabled="option.disabled"
                    :aria-label="
                      option.disabled
                        ? option.title + ' (unavailable)'
                        : undefined
                    "
                  />
                  <span class="margin-label">{{ option.title }}</span>
                </label>
              </template>
            </div>
          </fieldset>

          <!-- Hardware Section -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Hardware</legend>
            <div class="underline" aria-hidden="true"></div>
            <div
              v-for="option in hardwareOptions"
              :key="option.value"
              class="option-label"
              :class="{ 'disabled-item': option.disabled }"
            >
              <template v-if="option.value === 'all'">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    @change="handleAllSelection('hardware', $event)"
                    :checked="allSelected.hardware"
                  />
                  <span class="margin-label">All Hardware</span>
                </label>
              </template>
              <template v-else>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    v-model="localHardware"
                    :value="option.value"
                    @change="emitFilters"
                    :disabled="option.disabled"
                    :aria-label="
                      option.disabled
                        ? option.title + ' (unavailable)'
                        : undefined
                    "
                  />
                  <span class="margin-label">{{ option.title }}</span>
                </label>
              </template>
            </div>
          </fieldset>

          <!-- Duration Section -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Duration</legend>
            <div class="underline" aria-hidden="true"></div>
            <div
              v-for="option in durationOptions"
              :key="option.value"
              class="option-label"
              :class="{ 'disabled-item': option.disabled }"
            >
              <template v-if="option.value === 'all'">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    @change="handleAllSelection('duration', $event)"
                    :checked="allSelected.duration"
                  />
                  <span class="margin-label">All Duration</span>
                </label>
              </template>
              <template v-else>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    role="checkbox"
                    v-model="localDuration"
                    :value="option.value"
                    @change="emitFilters"
                    :disabled="option.disabled"
                    :aria-label="
                      option.disabled
                        ? option.title + ' (unavailable)'
                        : undefined
                    "
                  />
                  <span class="margin-label">{{ option.title }}</span>
                </label>
              </template>
            </div>
          </fieldset>
        </div>

        <!-- Tags Section -->
        <div>
          <!-- Tags use a group of toggle chips with role="checkbox" (WCAG 4.1.2 / 2.1.1) -->
          <fieldset class="zz-filter-column filter-fieldset">
            <legend class="filter-legend">Tags</legend>
            <div class="underline" aria-hidden="true"></div>
            <span
              class="mr-4 subject-item-2"
              style="
                font-size: var(--fs-small);
                font-weight: 400;
                margin-bottom: 10px;
              "
              v-for="(option, index) in tagOptions"
              :key="index"
              role="checkbox"
              :aria-checked="isSelected(option, localTags)"
              :aria-disabled="option.disabled || undefined"
              :tabindex="option.disabled ? -1 : 0"
              :class="{
                'subject-item-2-selected': isSelected(option, localTags),
                'disabled-item': option.disabled,
              }"
              @click="toggleSelection(option, localTags, true)"
              @keydown.space.prevent="toggleSelection(option, localTags, true)"
              @keydown.enter.prevent="toggleSelection(option, localTags, true)"
            >
              {{ option.title }} ({{ option.count }})
            </span>
          </fieldset>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  subjectOptions: {
    type: Array,
    default: () => [],
  },
  gradeOptions: {
    type: Array,
    default: () => [],
  },
  focusOptions: {
    type: Array,
    default: () => [],
  },
  hardwareOptions: {
    type: Array,
    default: () => [],
  },
  tagOptions: {
    type: Array,
    default: () => [],
  },
  subjects: {
    type: Array,
    default: () => [],
  },
  grades: {
    type: Array,
    default: () => [],
  },
  focus: {
    type: Array,
    default: () => [],
  },
  hardware: {
    type: Array,
    default: () => [],
  },
  duration: {
    type: Array,
    default: () => [],
  },
  tags: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["filtersChanged"]);

// Internal state management
const isOpen = ref(false);

// Local reactive copies of filter values
const localSubjects = ref([...props.subjects]);
const localGrades = ref([...props.grades]);
const localFocus = ref([...props.focus]);
const localHardware = ref([...props.hardware]);
const localDuration = ref([...props.duration]);
const localTags = ref([...props.tags]);

// Duration options
const durationOptions = ref([
  {
    value: "all",
    title: "All Duration",
    text: "All Duration",
    disabled: false,
    id: 0,
  },
  {
    value: 60,
    title: "60 minutes",
    text: "60 minutes",
    disabled: false,
    id: 1,
  },
  {
    value: 90,
    title: "90 minutes",
    text: "90 minutes",
    disabled: false,
    id: 2,
  },
  {
    value: 120,
    title: "120 minutes",
    text: "120 minutes",
    disabled: false,
    id: 3,
  },
]);

// Computed properties for filter state
const activeFiltersCount = computed(() => {
  return (
    localSubjects.value.length +
    localGrades.value.length +
    localFocus.value.length +
    localHardware.value.length +
    localDuration.value.length +
    localTags.value.length
  );
});

const hasActiveFilters = computed(() => {
  return activeFiltersCount.value > 0;
});

// State untuk "All" checkbox di masing-masing kategori
const allSelected = ref({
  grade: false,
  subject: false,
  focus: false,
  hardware: false,
  duration: false,
});

// Watch for prop changes to sync local state
watch(
  () => props.subjects,
  (newVal) => {
    localSubjects.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.grades,
  (newVal) => {
    localGrades.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.focus,
  (newVal) => {
    localFocus.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.hardware,
  (newVal) => {
    localHardware.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.duration,
  (newVal) => {
    localDuration.value = [...newVal];
  },
  { deep: true }
);

watch(
  () => props.tags,
  (newVal) => {
    localTags.value = [...newVal];
  },
  { deep: true }
);

const togglePopup = () => {
  isOpen.value = !isOpen.value;
};

const closePopup = () => {
  isOpen.value = false;
};

const emitFilters = () => {
  // Update "All" checkbox states
  allSelected.value.grade =
    localGrades.value.length ===
    props.gradeOptions.filter((option) => !option.disabled).length;
  allSelected.value.subject =
    localSubjects.value.length ===
    props.subjectOptions.filter((option) => !option.disabled).length;
  allSelected.value.focus =
    localFocus.value.length ===
    props.focusOptions.filter((option) => !option.disabled).length;
  allSelected.value.hardware =
    localHardware.value.length ===
    props.hardwareOptions.filter((option) => !option.disabled).length;
  allSelected.value.duration =
    localDuration.value.length ===
    durationOptions.value.filter((option) => !option.disabled).length;

  emit("filtersChanged", {
    subjects: props.subjectOptions.filter((option) =>
      localSubjects.value.includes(option.value)
    ),
    grades: props.gradeOptions.filter((option) =>
      localGrades.value.includes(option.value)
    ),
    focus: props.focusOptions.filter((option) =>
      localFocus.value.includes(option.value)
    ),
    hardwareAssociated: props.hardwareOptions.filter((option) =>
      localHardware.value.includes(option.value)
    ),
    duration: durationOptions.value.filter((option) =>
      localDuration.value.includes(option.value)
    ),
    tags: localTags.value,
  });
};

const toggleSelection = (item, list, isMultiple) => {
  if (isMultiple && !item.disabled) {
    const index = list.indexOf(item);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(item);
    }
  } else if (!isMultiple && !item.disabled) {
    if (list.includes(item)) {
      list.length = 0;
    } else {
      list.length = 0;
      list.push(item);
    }
  }

  emitFilters();
};

const isSelected = (item, list) => {
  return list.includes(item);
};

function handleAllSelection(category, event) {
  const isChecked = event.target.checked;

  switch (category) {
    case "grade":
      allSelected.value.grade = isChecked;
      localGrades.value = isChecked
        ? props.gradeOptions
            .filter((option) => !option.disabled)
            .map((option) => option.value)
        : [];
      break;
    case "subject":
      allSelected.value.subject = isChecked;
      localSubjects.value = isChecked
        ? props.subjectOptions
            .filter((option) => !option.disabled)
            .map((option) => option.value)
        : [];
      break;
    case "focus":
      allSelected.value.focus = isChecked;
      localFocus.value = isChecked
        ? props.focusOptions
            .filter((option) => !option.disabled)
            .map((option) => option.value)
        : [];
      break;
    case "hardware":
      allSelected.value.hardware = isChecked;
      localHardware.value = isChecked
        ? props.hardwareOptions
            .filter((option) => !option.disabled)
            .map((option) => option.value)
        : [];
      break;
    case "duration":
      allSelected.value.duration = isChecked;
      localDuration.value = isChecked
        ? durationOptions.value
            .filter((option) => !option.disabled)
            .map((option) => option.value)
        : [];
      break;
  }

  emitFilters();
}
</script>

<style scoped>
/* Visually-hidden utility – text read by screen readers but not visible (WCAG 1.3.1) */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.filter-popup-wrapper {
  display: inline-block;
}

/* Filter trigger button styling - matching MultipleSelect */
.filter-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;
  min-width: 120px;
  /* Reset native <button> styles so it looks identical to the old <div> */
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

/* Visible focus ring for keyboard users (WCAG 2.4.7) */
.filter-trigger:focus-visible {
  outline: 3px solid var(--blue-light-2, #4a90d9);
  outline-offset: 2px;
}

.filter-trigger:hover {
  border-color: var(--blue-light-2);
}

.filter-trigger.active {
  border: none;
  background-color: var(--blue-light-2);
}

.filter-trigger.active .filter-text,
.filter-trigger.active .dropdown-arrow {
  color: #ffffff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.filter-counter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: var(--primary-pink);
  padding-top: 2px;
  color: white;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
  animation: counter-appear 0.2s ease-out;
}

.filter-trigger.active .filter-counter {
  background: #ffffff;
  color: var(--primary-blue);
}

.filter-text {
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-blue);
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

/* Counter animation */
@keyframes counter-appear {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 4000;
  backdrop-filter: blur(2px);
}

.filter-panel-header {
  display: none;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
}

.filter-panel-title {
  margin: 0;
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
}

.close-button {
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  color: #6b7280;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.zz-filter-panel {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: #ffffff;
  padding: 20px 30px;
  border: 1px solid #ddd;
  z-index: 5000;
  border-radius: 10px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.zz-filter-panel-content {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.zz-filter-column {
  flex: 1;
  min-width: 200px;
}

.zz-filter-column div {
  margin-bottom: 5px;
  color: var(--primary-blue);
  font-weight: 600;
  font-size: var(--fs-small);
}

/* Reset fieldset / legend to keep existing visual design intact (WCAG 1.3.1) */
.filter-fieldset {
  border: none;
  margin: 0;
  padding: 0;
}

.filter-legend {
  margin-bottom: 5px;
  color: var(--primary-blue);
  font-weight: 600;
  font-size: var(--fs-small);
  padding: 0;
  float: left;
  width: 100%;
}

.underline {
  height: 1px;
  background-color: var(--primary-blue);
  margin-bottom: 10px;
  clear: both;
}

.option-label {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  width: 100%;
  padding: 4px 0;
  font-size: var(--fs-small);
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.margin-label {
  line-height: 1.4;
  word-break: break-word;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .filter-panel-header {
    display: flex;
  }

  .zz-filter-panel {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    padding: 20px 15px;
    border-radius: 0;
    max-height: none;
    overflow-y: auto;
  }

  .zz-filter-panel-content {
    flex-direction: column;
    gap: 25px;
  }

  .zz-filter-column {
    flex: none;
    min-width: auto;
    width: 100%;
  }

  .zz-filter-column div {
    font-size: var(--fs-medium);
    margin-bottom: 8px;
  }

  .underline {
    margin-bottom: 15px;
  }

  .option-label {
    margin-bottom: 12px;
  }

  .checkbox-label {
    padding: 8px 0;
    font-size: 15px;
    gap: 12px;
  }

  .checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
  }
}

@media (max-width: 480px) {
  .zz-filter-panel {
    padding: 15px 10px;
  }

  .zz-filter-panel-content {
    gap: 20px;
  }

  .zz-filter-column div {
    font-size: 15px;
  }

  .checkbox-label {
    font-size: var(--fs-small);
    padding: 6px 0;
  }
}

.disabled-item {
  opacity: 0.5;
  pointer-events: none;
}

.disabled-item .checkbox-label {
  cursor: not-allowed;
}

.subject-item-2 {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid #d8dae5;
  border-radius: 6px;
  font-size: 12px;
  text-align: center;
  cursor: pointer;
  font-weight: 500;
  margin-right: 8px;
  margin-bottom: 8px;
  font-style: italic;
  color: #3b4648;
  transition: all 0.2s ease;
  line-height: 1.2;
}

.subject-item-2:hover {
  border-color: var(--primary-blue);
}

.subject-item-2-selected {
  background-color: #f2f3fb;
  border-color: var(--primary-blue);
}

/* Filter popup transitions */
.filter-popup-enter-active {
  transition: all 0.3s ease;
}

.filter-popup-leave-active {
  transition: all 0.2s ease;
}

.filter-popup-enter-from {
  opacity: 0;
  transform: translateY(-10px) scale(0.95);
}

.filter-popup-leave-to {
  opacity: 0;
  transform: translateY(-5px) scale(0.98);
}

/* Mobile responsiveness for filter trigger */
@media (max-width: 768px) {
  .filter-trigger {
    padding: 8px 10px;
    gap: 6px;
    min-height: 36px;
    min-width: 100px;
  }

  .filter-counter {
    min-width: 20px;
    height: 20px;
    font-size: 11px;
  }

  .filter-text {
    font-size: 13px;
  }

  .dropdown-arrow {
    width: 10px;
    height: 6px;
  }
}

@media (max-width: 480px) {
  .filter-trigger {
    padding: 6px 8px;
    gap: 4px;
    min-height: 32px;
    min-width: 90px;
  }

  .filter-counter {
    min-width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .filter-text {
    font-size: 12px;
  }
}

/* Mobile responsive for tags */
@media (max-width: 768px) {
  .subject-item-2 {
    padding: 8px 12px;
    font-size: 13px;
    margin-right: 6px;
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .subject-item-2 {
    padding: 6px 10px;
    font-size: 12px;
    margin-right: 4px;
    margin-bottom: 8px;
  }
}
</style>
